import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiError } from '../../abstract/api-error';
import { AgremoHealthService } from '../../core/api-services/agremo-health.service';
import { AgremoUsageService } from '../../core/api-services/agremo-usage.service';
import { AgremoAnalysisService } from '../../core/data-services/agremo-analysis.service';
import { AgremoCreditService } from '../../core/data-services/agremo-credit.service';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal.component';
import { AgremoCreditBlock } from '../../core/data-models/agremo-credit-block.model';
import { AgremoCreditType } from '../../core/data-models/agremo-credit-type.model';
import { ACRES_PER_HECTARE } from '../../core/utils/agremo-utils';
import { AgremoUsageStats } from '../../core/response-models/agremo-usage-stats.model';
import { AgremoHealthStatus } from '../../core/response-models/agremo-health-status.model';
import { AgremoMapStatusCode } from '../../core/response-models/agremo-map-status-code.model';
import { AgremoCameraType } from '../../core/response-models/agremo-camera-type.model';
import { AgremoLayerType } from '../../core/response-models/agremo-layer-type.model';
import { AgremoLayerStatusCode } from '../../core/response-models/agremo-layer-status-code.model';
import { AgremoAnalysisTypeCodebook } from '../../core/response-models/agremo-analysis-type-codebook.model';
import { AgremoProcessPendingResult } from '../../core/response-models/agremo-process-pending-result.model';

/**
 * Agremo Credits Management Component
 *
 * Admin page for managing Agremo credit blocks, viewing usage statistics,
 * monitoring Agremo API health, and processing pending analyses.
 * Follows the admin chemicals list pattern.
 */
@Component({
  selector: 'app-agremo-credits',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmationModalComponent],
  templateUrl: './agremo-credits.component.html',
  styleUrl: './agremo-credits.component.scss'
})
export class AgremoCreditsComponent implements OnInit {
  /**
   * Exposed for templates that need to convert between acres and hectares
   * inline (e.g., the live unit-preview under the add-credit form). Keeps
   * the conversion constant single-sourced from the model — DON'T inline
   * `2.47105` literals in the HTML.
   */
  readonly ACRES_PER_HECTARE = ACRES_PER_HECTARE;

  // Credit blocks
  activeCredits: AgremoCreditBlock[] = [];
  archivedCredits: AgremoCreditBlock[] = [];
  showArchived = false;

  // Usage statistics
  usageStats: AgremoUsageStats | null = null;

  // Health status
  healthStatus: AgremoHealthStatus | null = null;
  healthLoading = true;

  // Codebooks
  mapStatuses: AgremoMapStatusCode[] = [];
  cameraTypes: AgremoCameraType[] = [];
  layerTypes: AgremoLayerType[] = [];
  layerStatuses: AgremoLayerStatusCode[] = [];
  analysisTypes: AgremoAnalysisTypeCodebook[] = [];
  codebooksLoading = false;
  codebooksError = '';

  // Loading states
  loading = true;
  usageLoading = true;
  errorMessage = '';
  successMessage = '';

  // New credit block form (2026-05-20 multi-unit design)
  // Admin picks a unit (acres or hectares) and enters the amount in that
  // unit. Server stores the value in the block's native unit; conversion
  // to acres happens at consume time. `analysis` is intentionally not in
  // the dropdown — those rows are read-only legacy data.
  showAddForm = false;
  newCreditAmount: number | null = null;
  newCreditType: AgremoCreditType = 'acres';
  newCreditExpiration = '';
  addingCredit = false;

  // Per-unit availability totals (populated from the credit list response)
  availableAcres = 0;
  availableHectares = 0;
  availableAnalyses = 0;

  // Process pending
  processingPending = false;

  // Confirmation modal
  showConfirmModal = false;
  confirmModalProcessing = false;
  archiveTargetId: string | null = null;
  archiveTargetCredits: AgremoCreditBlock | null = null;

  // Process pending confirmation
  showProcessConfirmModal = false;

  constructor(
    private creditService: AgremoCreditService,
    private analysisService: AgremoAnalysisService,
    private healthService: AgremoHealthService,
    private usageService: AgremoUsageService
  ) {}

  ngOnInit(): void {
    this.loadHealth();
    this.loadCodebooks();
    this.loadCredits();
    this.loadUsageStats();
  }

  // ============================================
  // HEALTH CHECK
  // ============================================

  loadHealth(): void {
    this.healthLoading = true;
    this.healthService.checkHealth().subscribe({
      next: (status) => {
        this.healthStatus = status;
        this.healthLoading = false;
      },
      error: (error) => {
        console.error('Failed to check Agremo health:', error);
        this.healthStatus = {
          connected: false,
          error: 'Failed to check connection',
          checkedAt: new Date().toISOString()
        };
        this.healthLoading = false;
      }
    });
  }

  // ============================================
  // AGREMO CODEBOOKS
  // ============================================

  loadCodebooks(): void {
    this.codebooksLoading = true;
    this.codebooksError = '';

    let completed = 0;
    const total = 5;
    const checkDone = () => {
      completed++;
      if (completed >= total) {
        this.codebooksLoading = false;
      }
    };

    this.healthService.getMapStatuses().subscribe({
      next: (statuses) => { this.mapStatuses = statuses; checkDone(); },
      error: (error) => { console.error('Failed to load map statuses:', error); this.codebooksError = 'Failed to load codebooks'; checkDone(); }
    });

    this.healthService.getCameraTypes().subscribe({
      next: (cameras) => { this.cameraTypes = cameras; checkDone(); },
      error: (error) => { console.error('Failed to load camera types:', error); this.codebooksError = 'Failed to load codebooks'; checkDone(); }
    });

    this.healthService.getLayerTypes().subscribe({
      next: (types) => { this.layerTypes = types; checkDone(); },
      error: (error) => { console.error('Failed to load layer types:', error); this.codebooksError = 'Failed to load codebooks'; checkDone(); }
    });

    this.healthService.getLayerStatuses().subscribe({
      next: (statuses) => { this.layerStatuses = statuses; checkDone(); },
      error: (error) => { console.error('Failed to load layer statuses:', error); this.codebooksError = 'Failed to load codebooks'; checkDone(); }
    });

    this.healthService.getAnalysisTypes().subscribe({
      next: (types) => { this.analysisTypes = types; checkDone(); },
      error: (error) => { console.error('Failed to load analysis types:', error); this.codebooksError = 'Failed to load codebooks'; checkDone(); }
    });
  }

  // ============================================
  // CREDIT BLOCKS
  // ============================================

  loadCredits(): void {
    this.loading = true;
    this.errorMessage = '';

    this.creditService.listCredits().subscribe({
      next: (response) => {
        const allCredits = response.credits || [];
        const now = new Date();

        this.activeCredits = allCredits.filter(c =>
          !c.archivedAt && !isExpired(c, now)
        );
        this.archivedCredits = allCredits.filter(c =>
          c.archivedAt || isExpired(c, now)
        );

        // Pre-2026-05-20 Hub builds don't return these fields — fall back
        // to client-side computation so the admin works against either.
        this.availableAcres = response.availableAcres
          ?? this.computeAvailableAcres(this.activeCredits);
        this.availableHectares = response.availableHectares
          ?? (this.availableAcres / ACRES_PER_HECTARE);
        this.availableAnalyses = response.availableAnalyses
          ?? this.activeCredits
            .filter(c => c.creditType === 'analysis')
            .reduce((sum, c) => sum + c.remainingCredits, 0);

        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load credit blocks:', error);
        this.errorMessage = ApiError.fromHttpError(error).message;
        this.loading = false;
      }
    });
  }

  /** Client-side acres-available fallback when Hub doesn't surface it. */
  private computeAvailableAcres(blocks: AgremoCreditBlock[]): number {
    return blocks.reduce((sum, b) => {
      if (b.creditType === 'acres') return sum + b.remainingCredits;
      if (b.creditType === 'hectares') return sum + b.remainingCredits * ACRES_PER_HECTARE;
      return sum; // 'analysis' blocks are not acre-convertible
    }, 0);
  }

  // ============================================
  // ADD CREDIT BLOCK
  // ============================================

  toggleAddForm(): void {
    this.showAddForm = !this.showAddForm;
    if (!this.showAddForm) {
      this.resetAddForm();
    }
  }

  submitNewCredit(): void {
    if (!this.newCreditAmount || this.newCreditAmount <= 0 || !this.newCreditExpiration) {
      return;
    }

    this.addingCredit = true;
    this.errorMessage = '';

    const amount = this.newCreditAmount;
    const creditType = this.newCreditType;

    this.creditService.createCredit({
      amount,
      creditType,
      expiresAt: new Date(this.newCreditExpiration).toISOString()
    }).subscribe({
      next: () => {
        const unitLabel = creditType === 'hectares' ? 'hectares' : 'acres';
        this.successMessage = `Credit block of ${amount} ${unitLabel} created successfully`;
        this.resetAddForm();
        this.showAddForm = false;
        this.addingCredit = false;
        this.loadCredits();
        this.loadUsageStats();
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to create credit block:', error);
        this.errorMessage = ApiError.fromHttpError(error).message;
        this.addingCredit = false;
      }
    });
  }

  private resetAddForm(): void {
    this.newCreditAmount = null;
    this.newCreditType = 'acres';
    this.newCreditExpiration = '';
  }

  // ============================================
  // ARCHIVE CREDIT BLOCK
  // ============================================

  confirmArchive(credit: AgremoCreditBlock): void {
    this.archiveTargetId = credit.id;
    this.archiveTargetCredits = credit;
    this.showConfirmModal = true;
  }

  onConfirmArchive(): void {
    if (!this.archiveTargetId) return;

    this.confirmModalProcessing = true;

    this.creditService.archiveCredit(this.archiveTargetId).subscribe({
      next: () => {
        this.successMessage = 'Credit block archived successfully';
        this.showConfirmModal = false;
        this.confirmModalProcessing = false;
        this.archiveTargetId = null;
        this.archiveTargetCredits = null;
        this.loadCredits();
        this.loadUsageStats();
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to archive credit block:', error);
        this.errorMessage = ApiError.fromHttpError(error).message;
        this.confirmModalProcessing = false;
        this.showConfirmModal = false;
      }
    });
  }

  onCancelArchive(): void {
    this.showConfirmModal = false;
    this.confirmModalProcessing = false;
    this.archiveTargetId = null;
    this.archiveTargetCredits = null;
  }

  // ============================================
  // USAGE STATISTICS
  // ============================================

  loadUsageStats(): void {
    this.usageLoading = true;

    this.usageService.getUsageStats().subscribe({
      next: (stats) => {
        this.usageStats = stats;
        this.usageLoading = false;
      },
      error: (error) => {
        console.error('Failed to load usage statistics:', error);
        this.usageLoading = false;
      }
    });
  }

  // ============================================
  // PROCESS PENDING
  // ============================================

  confirmProcessPending(): void {
    this.showProcessConfirmModal = true;
  }

  onConfirmProcessPending(): void {
    this.processingPending = true;
    this.showProcessConfirmModal = false;

    this.analysisService.processPending().subscribe({
      next: (result: AgremoProcessPendingResult) => {
        this.successMessage = `Processed ${result.processed} of ${result.totalPending} pending analyses. ${result.remainingPending} remaining.`;
        this.processingPending = false;
        this.loadCredits();
        this.loadUsageStats();
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to process pending analyses:', error);
        this.errorMessage = ApiError.fromHttpError(error).message;
        this.processingPending = false;
      }
    });
  }

  onCancelProcessPending(): void {
    this.showProcessConfirmModal = false;
  }

  // ============================================
  // HELPERS
  // ============================================

  get totalAvailableCredits(): number {
    return this.activeCredits.reduce((sum, c) => sum + c.remainingCredits, 0);
  }

  get totalUsedCredits(): number {
    return this.activeCredits.reduce((sum, c) => sum + this.usedCredits(c), 0);
  }

  /** Credits consumed from a block (a block whose total the hub didn't send counts as 0 used). */
  usedCredits(credit: AgremoCreditBlock): number {
    return Math.max(0, (credit.totalCredits ?? 0) - credit.remainingCredits);
  }

  /**
   * Dual-unit display helpers — Agremo's dashboard shows hectares but the
   * platform consumes in acres, so we surface both to make reconciliation
   * easy. Both formatters use `maximumFractionDigits: 2` (no minimum), so
   * whole numbers render without trailing zeros and fractional values get
   * 2 decimals.
   */
  formatAcres(acres: number): string {
    if (acres === 0) return '0';
    return acres.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  formatHectares(hectares: number): string {
    if (hectares === 0) return '0';
    return hectares.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  /** Convert a block's `remainingCredits` value into acres for display. */
  blockRemainingAsAcres(block: AgremoCreditBlock): number {
    if (block.creditType === 'hectares') return block.remainingCredits * ACRES_PER_HECTARE;
    if (block.creditType === 'acres') return block.remainingCredits;
    return 0; // 'analysis' blocks don't convert to acres
  }

  /** Convert a block's `remainingCredits` value into hectares for display. */
  blockRemainingAsHectares(block: AgremoCreditBlock): number {
    if (block.creditType === 'hectares') return block.remainingCredits;
    if (block.creditType === 'acres') return block.remainingCredits / ACRES_PER_HECTARE;
    return 0;
  }

  /**
   * Human-readable label for a block's unit. Defaults to 'units' when the
   * server returns an unrecognized or missing creditType (e.g., during a
   * pre/post-migration rollout window or if a future credit type is
   * surfaced before the admin UI knows about it) so the UI degrades to a
   * safe generic label instead of rendering `undefined` in the table.
   */
  unitLabel(block: AgremoCreditBlock): string {
    switch (block.creditType) {
      case 'acres': return 'acres';
      case 'hectares': return 'ha';
      case 'analysis': return 'analyses';
      default: return 'units';
    }
  }

  getUsagePercent(credit: AgremoCreditBlock): number {
    if (!credit.totalCredits) return 0;
    return Math.round((this.usedCredits(credit) / credit.totalCredits) * 100);
  }

  getUsageBarClass(credit: AgremoCreditBlock): string {
    const percent = this.getUsagePercent(credit);
    if (percent >= 90) return 'bg-danger';
    if (percent >= 70) return 'bg-warning';
    return 'bg-success';
  }

  isExpiringSoon(credit: AgremoCreditBlock): boolean {
    if (!credit.expiresAt) return false;
    const expiresAt = new Date(credit.expiresAt);
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    return expiresAt <= sevenDaysFromNow && expiresAt > new Date();
  }

  formatDate(dateString: string | Date | null | undefined): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string | Date | null | undefined): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private clearSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 5000);
  }
}

/** A block without an expiry never expires. */
function isExpired(credit: AgremoCreditBlock, now: Date): boolean {
  return !!credit.expiresAt && new Date(credit.expiresAt) <= now;
}
